import { publicImageHint } from './28-image-sources.mjs';
/** Opportunistic public image hints: five URLs per actor/context, no private
 * media or video. Hidden/metered surfaces start no work. Already-issued image
 * requests remain browser-owned and cannot always be cancelled. */
export function createFeedWarming(
  ctx,
  {
    visible = () => !document.hidden,
    connection = () => navigator.connection,
    image = () => new Image(),
    schedule = (run) => setTimeout(run, 250),
    cancel = clearTimeout,
  } = {},
) {
  let timer, key;
  const hinted = new Set();
  const canWarm = () => {
    const network = connection();
    return (
      !!ctx.state?.me?.id &&
      ['feed', 'learning'].includes(ctx.route()[0]) &&
      visible() &&
      !network?.saveData &&
      !/2g/.test(network?.effectiveType ?? '')
    );
  };
  function align() {
    const current = JSON.stringify([
      ctx.state?.me?.id,
      ctx.mode,
      ctx.contentScope ?? 'ordinary',
      ctx.discoveryPolicyVersion,
      ctx.feedScope,
      ctx.state?.settings?.learning,
      ctx.state?.settings?.interestEpoch,
    ]);
    if (current !== key) {
      dispose();
      key = current;
    }
  }
  function hints(urls) {
    align();
    if (!canWarm()) return;
    for (const source of urls) {
      const url = publicImageHint(source);
      if (hinted.size >= 5) break;
      if (!url || hinted.has(url)) continue;
      hinted.add(url);
      const node = image();
      node.decoding = 'async';
      node.fetchPriority = 'low';
      node.src = url;
    }
  }
  function warm(warmup, current, available) {
    align();
    if (timer || !canWarm()) return;
    const expected = key;
    timer = schedule(async () => {
      timer = undefined;
      align();
      if (expected !== key || !canWarm()) return;
      for (const format of available.filter((value) => value !== current)) {
        if (!canWarm() || expected !== key) return;
        await warmup.warm(format).catch(() => {});
      }
    });
  }
  function dispose() {
    if (timer) cancel(timer);
    timer = undefined;
    hinted.clear();
  }
  return { canWarm, hints, warm, dispose };
}
